import React, { useState } from "react";
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from "react-router-dom";
import {useDispatch} from "react-redux";
import {ThunkDispatch} from "@reduxjs/toolkit";
import { formatTimeAgo, parseTags, toFullWidthNumber } from "../../../utils/format";
import CustomIconButton from "../../../components/CustomIconButton";
import { setCanBackPrevPage } from "reducers/talentHome";
import { setJobCardBookmark } from "reducers/talentJob";
import { ITEM_TYPE } from "../../../constants/index";
import { MOBILE_SCREEN_WIDTH } from "../../../constants/index";

type JobCardProps = {
    items: any;
    handleBookmark?: (id: number) => void;
    customStyle: any;
    hideBookmark: boolean;
};

const JobCard = ({ items, handleBookmark, customStyle="", hideBookmark=false }: JobCardProps) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const isMobile = useMediaQuery({ maxWidth: MOBILE_SCREEN_WIDTH });
    const item = {
        id: items.id,
        title: items.title,
        job_name: (items.department ? items.department + " " : '') + items.occupation_2?.name + "職",
        tag: items.tag ? parseTags(String(items.tag)) : "",
        place: items.location_city?.label,
        annual_salary: "年収 " + `${items.annual_salary_min ? `${new Intl.NumberFormat().format(items.annual_salary_min)}` : 'ー'}` + " 〜 " + `${items.annual_salary_max ? `${new Intl.NumberFormat().format(items.annual_salary_max)} 万円` : 'ー'}`,
        public_date: formatTimeAgo(items.public_date),
        is_bookmarked: items.is_bookmarked,
    }
    const navigate = useNavigate();
    const handleBookmarkJob = (id: number) => {
        if (handleBookmark) {
            handleBookmark(id);
        } else {
            dispatch(setJobCardBookmark({ id: id, view: ITEM_TYPE.JOB, requestBody: {} }));
        }
    }

    const goDetail = (id: any) => {
        dispatch(setCanBackPrevPage(true));
        navigate("/talent/job/detail/" + id)
    }

    return (
        <div className={`relative h-full w-full flex flex-col justify-between rounded-2xl alu-section-box-shadow bg-white p-4 md:pt-8 gap-4 ${customStyle}`}>           
            {item.tag && isMobile && (
                <div className="flex flex-wrap items-center gap-2">
                    {item?.tag.map((tag: any, idx: number) => (
                    <span
                        key={idx}
                        className="border border-[#808080] rounded flex items-center justify-center py-[0.125rem] px-[0.375rem] text-xs alu-text-base-1"
                    >
                        {tag}
                    </span>
                    ))}
                </div>
            )}
            <h3
                className="text-xl font-bold alu-text-base-2 line-clamp-3 lg:line-clamp-3 text-ellipsis cursor-pointer"
                onClick={() => goDetail(item.id)}
            >
                {item.title}
            </h3>
            {item.tag && !isMobile && (
                <div className="flex flex-wrap items-center gap-2">
                    {item?.tag.map((tag: any, idx: number) => (
                    <span
                        key={idx}
                        className="border border-[#808080] rounded flex items-center justify-center py-[0.125rem] px-[0.375rem] text-xs alu-text-base-1"
                    >
                        {tag}
                    </span>
                    ))}
                </div>
            )}
            <div className="flex-1 flex flex-col justify-between">
                <div className="mb-4 flex flex-col gap-2">
                    <p className="text-base alu-text-base-2">
                        {item.job_name}
                    </p>
                    <div className="flex items-center gap-5">
                        <div className="flex items-center gap-1">
                        <span>
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            >
                            <mask
                                id="mask0_14003_28221"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="24"
                                height="24"
                            >
                                <rect width="24" height="24" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_14003_28221)">
                                <path
                                d="M3 21V7H7V3H17V11H21V21H13V17H11V21H3ZM5 19H7V17H5V19ZM5 15H7V13H5V15ZM5 11H7V9H5V11ZM9 15H11V13H9V15ZM9 11H11V9H9V11ZM9 7H11V5H9V7ZM13 15H15V13H13V15ZM13 11H15V9H13V11ZM13 7H15V5H13V7ZM17 19H19V17H17V19ZM17 15H19V13H17V15Z"
                                fill="#808080"
                                />
                            </g>
                            </svg>
                        </span>
                        <p className="text-base alu-text-base-2 w-[54px]">勤務地</p>
                        </div>
                        <p className="text-base alu-text-base-2">{item.place}</p>
                    </div>
                    <div className="flex items-center gap-5">
                        <div className="flex gap-1">
                            <span>
                                <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                >
                                <mask
                                    id="mask0_14003_28228"
                                    style={{ maskType: "alpha" }}
                                    maskUnits="userSpaceOnUse"
                                    x="0"
                                    y="0"
                                    width="24"
                                    height="24"
                                >
                                    <rect width="24" height="24" fill="#D9D9D9" />
                                </mask>
                                <g mask="url(#mask0_14003_28228)">
                                    <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM11.2857 16V18.8571H12.7143V16H16.2857V14.5714H12.7143V13.1429H16.2857V11.7143H13.375L17 6H15.3036L12 11.2143L8.69643 6H7L10.625 11.7143H7.71429V13.1429H11.2857V14.5714H7.71429V16H11.2857Z"
                                    fill="#808080"
                                    />
                                </g>
                                </svg>
                            </span>
                            <p className="text-base alu-text-base-2 w-[54px]">給与</p>
                        </div>
                        <p className="text-base alu-text-base-2">{item.annual_salary}</p>
                    </div>
                </div>
                
                <div className="flex items-center justify-between">
                    <p className="text-sm alu-text-base-1">{toFullWidthNumber(item.public_date)}</p>
                    {!hideBookmark && (
                    <CustomIconButton customStyle="shrink-0 group/bookmark" onClick={() => handleBookmarkJob(item.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_11236_7241)">
                                <path
                                    d={!item.is_bookmarked ? "M17 3H7C5.9 3 5.01 3.9 5.01 5L5 21L12 18L19 21V5C19 3.9 18.1 3 17 3ZM17 18L12 15.82L7 18V5H17V18Z" : "M17 3H7C5.9 3 5.01 3.9 5.01 5L5 21L12 18L19 21V5C19 3.9 18.1 3 17 3Z"}
                                    fill={!item.is_bookmarked ? "#808080" : "#FF861A"}
                                    className={!item.is_bookmarked ? "group-hover/bookmark:fill-[#FF861A] transition-colors duration-100 ease-in-out" : ""}
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_11236_7241">
                                <rect width="24" height="24" fill="white"/>
                                </clipPath>
                            </defs>
                        </svg>
                    </CustomIconButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobCard;
